// MagicLinkInvite — landing page for /invite/<token> emails.
// Reads invite metadata, shows the inviter context, and either:
//   - redirects to signup (carrying the token so post-signup auto-redeems)
//   - calls the redeem endpoint immediately if the user is already logged in
//
// This is the cross-device attribution fix — the token travels through
// the email so device-switching no longer drops attribution.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getToken } from "@/lib/api";

const PENDING_INVITE_KEY = "magic_link_pending_invite_token";

export default function MagicLinkInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const isLoggedIn = Boolean(getToken());

  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/invites/${token}`);
        if (!cancel) setInvite(r.data);
      } catch (e) {
        if (!cancel) setInvite({ status: "invalid", _error: e?.response?.data?.detail || "Couldn't load invite." });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  // Auto-redeem when the visitor is already authed
  useEffect(() => {
    if (!isLoggedIn || !invite || invite.status !== "pending" || redeemed) return;
    (async () => {
      setRedeeming(true);
      try {
        const r = await api.post(`/invites/${token}/redeem`, {});
        setRedeemed(true);
        if (r.data?.attribution_applied) {
          toast.success("Invitation accepted. Welcome aboard.");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't redeem invitation.");
      } finally {
        setRedeeming(false);
      }
    })();
  }, [isLoggedIn, invite, token, redeemed]);

  const goSignup = () => {
    // Stash the token so post-signup the dashboard can pick it up and redeem
    try { localStorage.setItem(PENDING_INVITE_KEY, token); } catch { /* ignore */ }
    // Pass inviter's referral code through the standard ?ref= signal so signup
    // attribution still works even if the post-signup redeem fails.
    const refCode = invite?.inviter_referral_code;
    nav(refCode ? `/signup?ref=${refCode}` : "/signup");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68]">
      Loading invitation…
    </div>
  );

  if (!invite || invite.status === "invalid" || invite.status === "expired") return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center px-4" data-testid="magic-link-invalid">
      <div className="max-w-md text-center">
        <div className="text-5xl mb-3">📭</div>
        <h1 className="font-serif-display text-2xl text-[#1C1B1A] mb-2">This invitation can't be opened</h1>
        <p className="text-sm text-[#6B6A68] mb-5">
          {invite?.status === "expired" ? "It expired before being used." : (invite?._error || "The link may have been removed or is no longer valid.")}
        </p>
        <Link to="/" className="text-sm text-[#C16E5A] hover:underline">Back to My Silo</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFCF8] px-4 py-12 flex items-center justify-center" data-testid="magic-link-invite-page">
      <div className="max-w-lg w-full">
        <div className="bg-white border border-[#E5E2DD] rounded-lg p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.22em] text-[#A89360]">
            <Sparkle size={14} weight="duotone" /> You're invited
          </div>
          <h1 className="font-serif-display text-3xl text-[#1C1B1A] leading-tight mb-3" data-testid="magic-link-headline">
            <strong>{invite.inviter_name}</strong> invited you to My Silo
          </h1>
          {invite.silo_name && (
            <p className="text-sm text-[#6B6A68] mb-2">
              They thought you'd like their silo: <strong className="text-[#1C1B1A]">{invite.silo_name}</strong>
            </p>
          )}
          {invite.note && (
            <blockquote className="my-4 pl-4 border-l-2 border-[#C16E5A] text-sm italic text-[#1C1B1A] leading-relaxed">
              "{invite.note}"
            </blockquote>
          )}
          <p className="text-sm text-[#6B6A68] leading-relaxed mb-6">
            My Silo is a platform for AI assistants trained only on what their creators choose. No noise, no hallucinations from the open web — just the source they trust.
          </p>

          {invite.status === "redeemed" || redeemed ? (
            <div className="rounded-md border border-[#4A5D4E]/30 bg-[#4A5D4E]/5 px-4 py-3 text-sm text-[#4A5D4E]" data-testid="magic-link-redeemed">
              ✓ Invitation accepted. Welcome.
              <Link to="/" className="ml-2 text-[#C16E5A] hover:underline">Continue →</Link>
            </div>
          ) : isLoggedIn ? (
            <div className="text-sm text-[#6B6A68]" data-testid="magic-link-redeeming">
              {redeeming ? "Accepting invitation…" : "Tap continue to accept."}
            </div>
          ) : (
            <button
              onClick={goSignup}
              data-testid="magic-link-accept-btn"
              className="w-full px-4 py-3 bg-[#1C1B1A] text-white text-sm rounded-md hover:bg-[#2C2B2A] transition flex items-center justify-center gap-2"
            >
              Accept invitation <ArrowRight size={14} />
            </button>
          )}

          {!isLoggedIn && (
            <div className="mt-3 text-xs text-[#6B6A68] text-center">
              Already have an account? <Link to="/login" className="text-[#C16E5A] hover:underline">Sign in</Link> first — we'll attach the invitation automatically.
            </div>
          )}
        </div>

        <p className="text-[11px] text-[#A89360] text-center mt-4">
          Expires {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "soon"}.
        </p>
      </div>
    </div>
  );
}

export { PENDING_INVITE_KEY };
